import { inject, Injectable } from '@angular/core';
import { YmodemStorageService, FileRecord, FileMetadata, UploadResult } from './ymodem-storage.service';
import { ApiClientService } from '@services/api-client';
import { IndexedDbStorageService } from './indexed-db-storage.service';
import { AuthenticationService } from '@services/authentication/authentication.service';

export type UploadStatus = {
    pending: number;
    uploading: number;
    completed: number;
    failed: number;
    totalFiles: number;
};

@Injectable({
    providedIn: 'root'
})
export class YmodemUploadService {
    private maxRetries = 3;
    private retryDelay = 5000; // 5 seconds
    private isUploading = false;
    private queuedDuringUpload = false;
    private skipUpload = false;
    private readonly staleFileAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    private readonly apiClientService = inject(ApiClientService);
    private readonly indexedDbStorage = inject(IndexedDbStorageService);
    private readonly authService = inject(AuthenticationService);

    public constructor(private storageService: YmodemStorageService) {
        void this.initFromStorage();
    }

    private getCurrentUserContext(): { userId: string | undefined; tenantId: string | undefined } {
        const user = this.authService.getUser();
        if (!user) {
            //console.warn('User not authenticated - cannot filter files by user/tenant');
            return { userId: undefined, tenantId: undefined };
        }
        return {
            userId: user.userId,
            tenantId: user.representing.id
        };
    }

    private async initFromStorage(): Promise<void> {
        console.log('Loading file content from IndexedDB...');

        // Clean up stale files (older than 7 days) for all users
        await this.cleanupStaleFiles();

        const { userId, tenantId } = this.getCurrentUserContext();

        // Get storage estimate
        const estimate = await this.indexedDbStorage.getStorageEstimate();
        const usageMB = (estimate.usage / 1024 / 1024).toFixed(2);
        const quotaMB = (estimate.quota / 1024 / 1024).toFixed(2);
        const quotaGB = (estimate.quota / 1024 / 1024 / 1024).toFixed(2);
        console.log(`IndexedDB Storage: ${usageMB} MB used of ${quotaMB} MB (${quotaGB} GB) available`);

        // Load only files for current user/tenant
        const records = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
        const allRecords = await this.storageService.getPendingUploads();
        console.log(
            `Checking ${records.length} file record(s) for current user/tenant (${allRecords.length} total records)...`
        );

        let totalSizeKB = 0;

        for (const record of records) {
            if (!record.id) {
                console.warn('Record with missing ID detected:', record);
                continue;
            }

            // Check all non-completed files for content availability
            if (record.status !== 'completed' && record.status !== 'duplicate') {
                const content = await this.indexedDbStorage.getItem(record.id);
                if (content) {
                    const sizeKB = (content.length / 1024).toFixed(2);
                    totalSizeKB += content.length / 1024;
                    console.log(`  ✓ Found content for ID ${record.id} (${sizeKB} KB): ${record.metadata.filename}`);

                    // Reset uploading → pending so the upload loop picks it up cleanly.
                    if (record.status === 'uploading') {
                        await this.storageService.updateUploadStatus(record.id, 'pending');
                    }
                    // If status is 'failed', keep the status but content is available for manual retry
                } else {
                    // Content is missing - mark as failed with specific error message
                    console.warn(`  ✗ Content missing for file ID ${record.id}: ${record.metadata.filename}`);
                    await this.storageService.updateUploadStatus(
                        record.id,
                        'failed',
                        'Content unavailable after reload — please retransfer'
                    );
                }
            }
        }

        console.log(`Total content loaded: ${totalSizeKB.toFixed(2)} KB (${(totalSizeKB / 1024).toFixed(2)} MB)`);

        // Note: Do not auto-start upload on init. Let user explicitly trigger uploads via "Retry upload" button.
        // This allows users to review queued files and control when uploads occur (important for offline mode).
    }

    private async cleanupCompletedContent(): Promise<void> {
        // Clean up content for completed/duplicate files from IndexedDB
        // Files that are completed/duplicate have already been uploaded to the server, so it's safe to clean up
        // regardless of user context. This frees up storage space for all users.
        const records = await this.storageService.getPendingUploads();
        const completedIds = records
            .filter((r) => r.status === 'completed' || r.status === 'duplicate')
            .map((r) => r.id)
            .filter((id): id is number => id !== undefined);

        for (const id of completedIds) {
            try {
                await this.indexedDbStorage.removeItem(id);
            } catch (error) {
                console.warn(`Failed to remove completed file ${id} from IndexedDB:`, error);
            }
        }

        if (completedIds.length > 0) {
            console.log(`🧹 Cleaned up ${completedIds.length} completed/duplicate file(s) from IndexedDB`);
        }
    }

    private async cleanupStaleFiles(): Promise<void> {
        // Clean up files older than 7 days (pending/failed files that haven't been processed)
        // This runs automatically on service initialization to free up storage for all users.
        const now = Date.now();
        const records = await this.storageService.getPendingUploads();
        const staleIds = records
            .filter((r) => {
                // Only clean up pending and failed files
                if (r.status !== 'pending' && r.status !== 'failed') {
                    return false;
                }
                // Check if file is older than 7 days
                return now - r.timestamp > this.staleFileAge;
            })
            .map((r) => r.id)
            .filter((id): id is number => id !== undefined);

        for (const id of staleIds) {
            try {
                // Remove content from IndexedDB
                await this.indexedDbStorage.removeItem(id);
                // Remove the record from storage
                await this.storageService.removeUploadedFile(id);
            } catch (error) {
                console.warn(`Failed to remove stale file ${id}:`, error);
            }
        }

        if (staleIds.length > 0) {
            const ageText = `${this.staleFileAge / (24 * 60 * 60 * 1000)} days`;
            console.log(
                `🧹 Cleaned up ${staleIds.length} stale file(s) older than ${ageText} from IndexedDB and records`
            );
        }
    }

    /**
     * Set whether to skip uploading files to the server.
     * When true, files will be queued locally but not automatically uploaded.
     * Useful for deferring uploads when experiencing poor connection.
     */
    public setSkipUpload(skip: boolean): void {
        this.skipUpload = skip;
        console.log(`Skip upload set to: ${skip}`);
    }

    // Mock server upload function - replace with actual API call when server is ready
    private async uploadToServer(
        fileContent: string,
        metadata: FileMetadata /*,
    rawBytes?: number[]*/
    ): Promise<UploadResult> {
        console.log(`Starting upload for ${metadata.filename}...`);

        //try {
        const response = await this.apiClientService.uploadMeasurement$({ content: fileContent, metadata }).toPromise();

        if (response.error && !response.duplicate) {
            throw new Error(`Upload failed: ${response.error}`);
        }

        return {
            success: response.imported || response.duplicate,
            duplicate: response.duplicate,
            error: response.error
        };
    }

    // Start upload process for all pending files
    public async startUploadProcess(): Promise<void> {
        if (this.isUploading) {
            console.log('Upload process already running');
            return;
        }

        // Skip upload process if flag is set (defer to better connection)
        if (this.skipUpload) {
            console.log('Upload skipped: skipUpload flag is enabled. Files remain queued for manual retry.');
            return;
        }

        this.isUploading = true;
        console.log('Starting upload process...');

        try {
            const { userId, tenantId } = this.getCurrentUserContext();
            const pendingFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
            const pendingToUpload = pendingFiles.filter(
                (f) => f.status === 'pending' //&& (f.retryCount || 0) < this.maxRetries
            );

            console.log(`Found ${pendingToUpload.length} files to upload`);

            for (const file of pendingToUpload) {
                await this.uploadSingleFile(file);
            }
        } finally {
            this.isUploading = false;
            console.log('Upload process completed');
            if (this.queuedDuringUpload) {
                this.queuedDuringUpload = false;
                setTimeout((): void => void this.startUploadProcess(), 100);
            }
        }
    }

    // Upload a single file with retry logic
    private async uploadSingleFile(fileRecord: FileRecord): Promise<void> {
        if (!fileRecord.id) {
            console.error('Cannot upload file: missing ID', fileRecord);
            return;
        }

        const data = await this.indexedDbStorage.getItem(fileRecord.id);
        if (data === null || data === undefined) {
            console.error(`File content not found in IndexedDB for ID ${fileRecord.id}`);
            await this.storageService.updateUploadStatus(fileRecord.id, 'failed', 'File content not found in storage');
            return;
        }

        try {
            console.log(`Uploading file ${fileRecord.id}: ${fileRecord.metadata.filename}`);
            await this.storageService.updateUploadStatus(fileRecord.id, 'uploading');

            const result = await this.uploadToServer(data, fileRecord.metadata);

            if (result.success) {
                console.log(`Upload successful for ID ${fileRecord.id}, removing from IndexedDB`);
                await this.indexedDbStorage.removeItem(fileRecord.id);
                if (result.duplicate) {
                    await this.storageService.updateUploadStatus(fileRecord.id, 'duplicate');
                } else {
                    await this.storageService.updateUploadStatus(fileRecord.id, 'completed');
                }
                console.log(`✓ Successfully uploaded file ${fileRecord.id} (duplicate: ${result.duplicate})`);
            } else {
                await this.storageService.updateUploadStatus(fileRecord.id, 'failed', result.error || 'Upload failed');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`Upload failed for file ${fileRecord.id}:`, errorMessage);

            if (fileRecord.retryCount >= this.maxRetries - 1) {
                // Max auto-retries reached — stop scheduling, but keep content so manual retry still works.
                await this.storageService.updateUploadStatus(fileRecord.id, 'failed', errorMessage);
                console.log(
                    `❌ File ${fileRecord.id} marked as failed after ${this.maxRetries} attempts (content kept in IndexedDB for manual retry)`
                );
            } else {
                await this.storageService.updateUploadStatus(fileRecord.id, 'pending', errorMessage);
                console.log(
                    `File ${fileRecord.id} will be retried (attempt ${fileRecord.retryCount + 1}/${this.maxRetries})`
                );

                setTimeout((): void => void this.startUploadProcess(), this.retryDelay);
            }
        }
    }

    // Queue a file for background upload
    public async queueFileForUpload(fileContent: string, metadata: FileMetadata): Promise<number> {
        const sizeKB = (fileContent.length / 1024).toFixed(2);
        const sizeMB = (fileContent.length / 1024 / 1024).toFixed(2);
        console.log(`Queueing file for upload: ${metadata.filename} (${sizeKB} KB / ${sizeMB} MB)`);

        // Capture current user/tenant context
        const { userId, tenantId } = this.getCurrentUserContext();
        if (!userId || !tenantId) {
            console.error('Cannot queue file - user context missing');
            throw new Error('User context missing - cannot queue file for upload');
        }

        const fileId = await this.storageService.storeTransferredFile(metadata, userId, tenantId);

        // Store content in IndexedDB
        await this.indexedDbStorage.setItem(fileId, fileContent);

        // Clean up old completed files to free up space
        await this.cleanupCompletedContent();

        // Show storage usage
        const estimate = await this.indexedDbStorage.getStorageEstimate();
        const usageMB = (estimate.usage / 1024 / 1024).toFixed(2);
        const quotaGB = (estimate.quota / 1024 / 1024 / 1024).toFixed(2);
        console.log(`✓ File ${metadata.filename} queued with ID: ${fileId} (user: ${userId}, tenant: ${tenantId})`);
        console.log(`  IndexedDB: ${usageMB} MB used of ${quotaGB} GB available`);

        // Start upload process if not already running
        if (!this.isUploading) {
            console.log('Starting upload process from queueFileForUpload');
            setTimeout((): void => void this.startUploadProcess(), 100);
        } else {
            this.queuedDuringUpload = true;
            console.log('Upload process already running, file will be processed in next batch');
        }

        return fileId;
    }

    // Get upload status summary
    public async getUploadStatus(): Promise<UploadStatus> {
        const { userId, tenantId } = this.getCurrentUserContext();
        const pendingFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);

        return {
            pending: pendingFiles.filter((f) => f.status === 'pending').length,
            uploading: pendingFiles.filter((f) => f.status === 'uploading').length,
            completed: pendingFiles.filter((f) => f.status === 'completed' || f.status === 'duplicate').length,
            failed: pendingFiles.filter((f) => f.status === 'failed').length,
            totalFiles: pendingFiles.length
        };
    }

    // Retry all failed uploads
    public async retryFailedUploads(): Promise<void> {
        console.log('Retrying failed uploads...');

        const { userId, tenantId } = this.getCurrentUserContext();
        const pendingFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
        const failedFiles = pendingFiles.filter((f) => f.status === 'failed');

        // Reset failed files to pending status
        for (const file of failedFiles) {
            if (file.id) {
                await this.storageService.updateUploadStatus(file.id, 'pending');
            }
        }

        // Start upload process
        await this.startUploadProcess();
    }

    /**
     * Retry uploads for failed and pending files (e.g., after offline mode).
     * Ensures skipUpload is disabled before starting the upload process.
     */
    public async retryUpload(): Promise<void> {
        console.log('Starting upload for failed and pending files...');

        // Ensure skipUpload is disabled when user explicitly triggers upload
        this.skipUpload = false;

        const { userId, tenantId } = this.getCurrentUserContext();
        const pendingFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
        const failedFiles = pendingFiles.filter((f) => f.status === 'failed');

        // Reset failed files to pending status
        for (const file of failedFiles) {
            if (file.id) {
                await this.storageService.updateUploadStatus(file.id, 'pending');
            }
        }

        // Start upload process for both failed (now pending) and existing pending files
        await this.startUploadProcess();
    }

    // Clear all stored files for the current user only
    public async clearAllFiles(): Promise<void> {
        const { userId, tenantId } = this.getCurrentUserContext();
        const allFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);

        // Remove content from IndexedDB for current user's files
        for (const file of allFiles) {
            if (file.id) {
                try {
                    await this.indexedDbStorage.removeItem(file.id);
                } catch (error) {
                    console.warn(`Failed to remove file ${file.id} from IndexedDB:`, error);
                }
                // Remove the record from storage
                await this.storageService.removeUploadedFile(file.id);
            }
        }

        if (allFiles.length > 0) {
            console.log(`Cleared ${allFiles.length} file(s) for user ${userId} / tenant ${tenantId}`);
        }
    }

    // Get detailed file list
    public async getFileList(): Promise<FileRecord[]> {
        const { userId, tenantId } = this.getCurrentUserContext();
        return this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
    }

    public async cleanupUploadedFiles(): Promise<void> {
        const { userId, tenantId } = this.getCurrentUserContext();
        const allFiles = await this.storageService.getPendingUploadsByUserTenant(userId, tenantId);
        const filesToDelete = allFiles.filter((f) => f.status === 'completed' || f.status === 'duplicate');

        for (const file of filesToDelete) {
            if (file.id) {
                // Remove content from IndexedDB
                try {
                    await this.indexedDbStorage.removeItem(file.id);
                } catch (error) {
                    console.warn(`Failed to remove file ${file.id} from IndexedDB:`, error);
                }
                // Remove the record
                await this.storageService.removeUploadedFile(file.id);
            }
        }

        if (filesToDelete.length > 0) {
            console.log(`Cleaned up ${filesToDelete.length} old uploaded file(s) from IndexedDB and records`);

            // Show updated storage
            const estimate = await this.indexedDbStorage.getStorageEstimate();
            const usageMB = (estimate.usage / 1024 / 1024).toFixed(2);
            console.log(`  IndexedDB usage after cleanup: ${usageMB} MB`);
        }
    }
}
